import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {ButtonActionVDesignBlock, CreateDesignBlockData, DesignBlockData} from "../../types/base-design-block.types";
import {defaultDesignBlockData} from "../../configs/default-design-block-data";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {BaseDesignBlockModalService} from "../../services/base-design-block-modal.service";
import {ModalsService} from "../../../../../../../../core/services/modals.service";
import {BaseDesignBlockPathStateService} from "../../services/base-design-block-path-state.service";
import {RecordData, RecordUpdateRequestParams} from "../../../../../../../../core/types/records-table.types";
import {designBlockFormFields} from "../../configs/design-block-form-fields";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";

@Component({
  selector: 'div[app-base-design-block-form]',
  imports: [
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatFormField
  ],
  templateUrl: './base-design-block-form.component.html',
  styleUrl: './base-design-block-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseDesignBlockFormComponent {
  designBlockData = input<DesignBlockData>(defaultDesignBlockData);
  designBlockFormGroup = input<FormGroup>(new FormGroup({}));
  parentId =  input<number>(0);
  isEditModeActive =  input<boolean>(false);
  columnKey =  input<string>('');
  noFirstData =  input<boolean>(false);
  updateDesignBlock = output<DesignBlockData>();
  deleteDesignBlock = output<void>();

  private baseDesignBlockModalService: BaseDesignBlockModalService = inject(BaseDesignBlockModalService);
  private modalService: ModalsService = inject(ModalsService);
  private baseDesignBlockPathStateService: BaseDesignBlockPathStateService = inject(BaseDesignBlockPathStateService);

  public readonly designBlockFormFields = designBlockFormFields;

  public getFormFieldClass(): string {
    if (!this.designBlockData().id) { return ''; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public async handleValueChange(key: string): Promise<void> {
    if (!this.designBlockData().id) { return; }

    const params: RecordUpdateRequestParams = {
      path: this.baseDesignBlockPathStateService.pathState(),
      recordId: this.designBlockData().id,
      fieldName: key,
      value: this.designBlockFormGroup().get(key)?.value
    };

    const data = await this.baseDesignBlockModalService.updatedRecordField<DesignBlockData>(params).catch(() => this.designBlockData());
    this.updateDesignBlock.emit(data);
  }

  public async handleClick(key: ButtonActionVDesignBlock): Promise<void> {
    const actionMap = {
      save: () => this.createData(),
      delete: () => this.deleteDesignBlock.emit(),
      openModal: () => this.openModal(),
      history: () => this.openHistoryModal(),
      deleteCurrent: () => this.deleteCurrent()
    } as const;

    await actionMap[key]?.();
  }

  private async createData(): Promise<void> {
    const params: CreateDesignBlockData = {
      designBlockId: this.parentId(),
      ...this.designBlockFormGroup().value
    };

    const data = await this.baseDesignBlockModalService.createData(params).catch(() => defaultDesignBlockData);
    this.updateDesignBlock.emit(data);
  }

  private async openModal(): Promise<void> {
    const data: RecordData = {
      id: this.parentId(),
      [this.columnKey()]: this.designBlockData(),
      modalKey: this.columnKey()
    };

    this.modalService.openDesignBlockFilesModal(data, this.columnKey());
  }

  private async openHistoryModal(): Promise<void> {
    this.modalService.openHistoryChangesModal({
      historyChangesData: this.designBlockData().history,
      columnKey: this.columnKey()
    });
  }

  private async deleteCurrent(): Promise<void> {
    await this.baseDesignBlockModalService.deleteRecord(this.designBlockData().id)
      .then(() => this.deleteDesignBlock.emit());
  }
}
